// Thread pane header (docs/06-ux-rules.md "Thread" / "Header"; P5 D4).
// "Order #{N}" · primary Mark-as-resolved/Unresolve action (a text link,
// not a filled button, per prototype v70 — VISUAL-SPEC.md §2) · <1200px
// "Details" toggle opening the rail drawer. No status pill in the title (P5
// D4). No overflow menu: Restrict and the "Restricted" pill live in the
// rail's Customer tab (FR-67, prototype v83).
import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import type { ItemStatus, RatingFilter } from 'api/feedbackInbox.types'
import { PermissionTooltip } from 'components/Navbar/Bar/PermissionTooltip'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import { useStyletron } from 'styletron-react'
import { useIsDesktop } from 'utilities/responsive'
import { useViewerCan } from '..'
import { useInboxRailControls } from '../InboxLayout'
import { textLinkButtonOverrides } from '../textLinkButtonOverrides'

const t = buildTranslate('feedback_inbox.thread')
const tComposer = buildTranslate('feedback_inbox.composer')

export interface ThreadHeaderProps {
  orderNumber: string | null
  status: ItemStatus
  rating: RatingFilter
  isPending: boolean
  onResolve: () => void
  onUnresolve: () => void
}

const ThreadHeader = ({
  orderNumber,
  status,
  rating,
  isPending,
  onResolve,
  onUnresolve,
}: ThreadHeaderProps) => {
  const [css] = useStyletron()
  const viewerCan = useViewerCan()
  const isDesktop = useIsDesktop()
  const { railOpen, openRail, closeRail } = useInboxRailControls()

  const permissionReason = viewerCan.manage
    ? undefined
    : tComposer('view_only_tooltip')

  const primaryLabel = status === 'open' ? t('mark_resolved') : t('unresolve')
  const primaryAction = status === 'open' ? onResolve : onUnresolve
  // 👍 rows render the closed-copy note only — no resolve/unresolve action
  // (docs/06-ux-rules.md "Thread" D7: "guest item, no composer, no
  // actions"). The mobile Details toggle isn't an "action" in that sense
  // (it opens the order/customer rail, which still shows for 👍 per
  // prototype v69's unconditional `renderOrderPanel`) — it stays.
  const showActions = rating !== 'up'
  const detailsToggleExpanded = railOpen
  const onDetailsToggleClick = railOpen ? closeRail : openRail

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '14px',
        padding: '16px 22px',
        borderBottom: `1px solid ${palette.grey20}`,
      })}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          minWidth: 0,
        })}
      >
        <Text.Header4 tag="span" color="grey90">
          {orderNumber
            ? t('order_title', { orderNumber })
            : t('order_title_fallback')}
        </Text.Header4>
      </div>
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          flexShrink: 0,
        })}
      >
        {!isDesktop && (
          <Button
            kind="minimal"
            aria-label={t('details_toggle_aria')}
            aria-expanded={detailsToggleExpanded}
            onClick={onDetailsToggleClick}
          >
            {t('details_toggle')}
          </Button>
        )}
        {showActions && (
          // `inline`: this wrapper sits in a flex row alongside the
          // Details toggle — PermissionTooltip's default `d-block w-100`
          // target would otherwise compete for 100% width and shrink its
          // siblings (same precedent as CaseDetail/index.tsx).
          <PermissionTooltip
            disabled={!viewerCan.manage}
            reason={permissionReason}
            id="feedback-inbox-thread-header-primary-perm"
            inline
            // Stops the wrapper's reason from overriding "Mark as
            // resolved"/"Unresolve"'s own accessible name; the reason moves
            // to aria-describedby instead (same as the composer's Add
            // reward toggle).
            srOnlyReason
          >
            <Button
              kind="minimal"
              onClick={primaryAction}
              disabled={!viewerCan.manage || isPending}
              overrides={textLinkButtonOverrides()}
            >
              {primaryLabel}
            </Button>
          </PermissionTooltip>
        )}
      </div>
    </div>
  )
}

export default ThreadHeader
